// Schema.org JSON-LD builders for rich results (Organization, Product, Offer,
// BreadcrumbList, FAQPage). Each builder returns a standalone block with its own
// @context so it can be injected as a separate <script type="application/ld+json">.
// URLs are absolute against the canonical production host.
export const SITE_URL = "https://www.autocarex.in";

const abs = (u) => (!u ? u : u.startsWith("http") ? u : `${SITE_URL}${u}`);

// Real, verified AutoCareX social/marketplace profiles (kept in sync with the
// footer/Contact SocialLinks component). Used for the Organization `sameAs`
// knowledge-graph signal — only genuine profiles, nothing fabricated.
export const SOCIAL_PROFILES = [
  "https://www.instagram.com/autocarexindia/",
  "https://www.facebook.com/profile.php?id=61590869154174",
  "https://www.youtube.com/@AutoCareXIndia",
  "https://x.com/care_x90888",
  "https://www.linkedin.com/company/autocarex/",
  "https://www.indiamart.com/jtmaxindiaenterprises",
];

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "F-440, F Block, Sector 63",
  addressLocality: "Noida",
  addressRegion: "Uttar Pradesh",
  postalCode: "201301",
  addressCountry: "IN",
};

// Sitewide Organization (rendered once per page via Helmet in Layout).
export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "AutoCareX",
  legalName: "JT MAX INDIA ENTERPRISES",
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  image: `${SITE_URL}/logo512.png`,
  description:
    "AutoCareX is an Indian car care brand offering professional-grade, DIY-friendly products — car shampoo, wax, polish, interior, wheel and glass cleaners — designed to protect, restore and elevate every drive.",
  email: "help@autocarex.in",
  telephone: "+91-8796027362",
  vatID: "09AAPFJ4216J1Z1",
  taxID: "09AAPFJ4216J1Z1",
  address: POSTAL_ADDRESS,
  sameAs: SOCIAL_PROFILES,
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-8796027362",
    email: "help@autocarex.in",
    contactType: "customer support",
    areaServed: "IN",
    availableLanguage: ["en", "hi"],
  },
});

// WebSite entity + on-site SearchAction (Sitelinks Search Box / AI search hints).
// Search resolves to the existing /shop?search= query the Shop page already reads.
export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "AutoCareX",
  description: "Intelligent car care products in India.",
  inLanguage: "en-IN",
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

// LocalBusiness/Store entity — strengthens local + entity signals. Opening hours
// mirror the real hours published on the Contact page (no fabricated data).
export const localBusinessLd = () => ({
  "@context": "https://schema.org",
  "@type": "Store",
  "@id": `${SITE_URL}/#localbusiness`,
  name: "AutoCareX",
  image: `${SITE_URL}/logo512.png`,
  url: SITE_URL,
  telephone: "+91-8796027362",
  email: "help@autocarex.in",
  priceRange: "₹₹",
  address: POSTAL_ADDRESS,
  parentOrganization: { "@id": `${SITE_URL}/#organization` },
  sameAs: SOCIAL_PROFILES,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "10:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "11:00",
      closes: "16:00",
    },
  ],
});

// Product schema. No aggregateRating / review fields (there are no real reviews
// yet and fabricating them violates Google policy). Price/currency are omitted
// when the product has no price.
export const productLd = (p) => {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    image: (p.images || []).map(abs),
    description: p.longDescription || p.shortDescription || p.description || p.name,
    brand: { "@type": "Brand", name: "AutoCareX" },
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/product/${p.slug}`,
      availability:
        p.inStock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "AutoCareX" },
    },
  };
  if (p.sku) ld.sku = p.sku;
  const hasPrice =
    p.price !== undefined && p.price !== null && p.price !== "" && !Number.isNaN(Number(p.price));
  if (hasPrice) {
    ld.offers.priceCurrency = "INR";
    ld.offers.price = String(p.price);
  }
  return ld;
};

// items: [{ name, url }] in order. Positions are assigned automatically.
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: (items || []).map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

// items: [{ q, a }] — only ground these in content already visible on the page.
export const faqLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (items || []).map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

// CollectionPage + embedded ItemList for category/shop listings. Helps search
// engines and AI systems understand the product taxonomy on listing pages.
export const collectionPageLd = ({ name, description, url, products }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url: url || `${SITE_URL}/shop`,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#organization` },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: (products || []).length,
    itemListElement: (products || []).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}/product/${p.slug}`,
      name: p.name,
    })),
  },
});
